import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import initTealiumTracker from 'tealium-tracker'
import { teliumDefaults } from '../../tracking/telium'
import { useInView } from 'react-intersection-observer'

// Components
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import AccordionItem from '../AccordionItem'
import AnimateSlideText from '../animation/AnimateSlideText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Styles
import { AccordionWrapper, AccordionList, AccordionLink } from './index.style'
import RichText from '../RichText'
import { colors } from '../../styles/vars/colors.style'
import Button from '../Button'

const AccordionSection = ({
  id,
  heading,
  content,
  button,
  linkId,
  faq,
  bottomPadding = false,
  defaultOpen,
  background,
}) => {
  const [ref, inView] = useInView({ triggerOnce: true })

  useEffect(() => {
    const trackFAQSection = () => {
      const { trackPageLoad } = initTealiumTracker()

      trackPageLoad({
        pageName: 'ultimatecruise:faq',
        ...teliumDefaults,
      })
    }

    if (inView && faq) trackFAQSection()
  }, [inView, faq])

  const loc = process.env.GATSBY_CONTENTFUL_LOCALE || 'en-US'

  return (
    <ScrollSection>
      <AccordionWrapper id={linkId} background={background} ref={ref}>
        <Spacer size={[100, 250]} />

        <Container>
          <Grid>
            <GridItem
              tiny={12}
              tabletP={loc === 'en-US' ? 12 : 10}
              tabletPStart={loc === 'en-US' ? 1 : 2}
              tabletL={8}
              tabletLStart={3}
              desk={6}
              deskStart={4}
            >
              <RichText id={`${id}-heading`} content={heading} delayEm={true} />
              <Spacer size={22} />
            </GridItem>
            {loc === 'en-US' && (
              <GridItem
                tiny={12}
                tabletP={loc === 'en-US' ? 12 : 10}
                tabletPStart={loc === 'en-US' ? 1 : 2}
                tabletL={8}
                tabletLStart={3}
                desk={6}
                deskStart={4}
              >
                <AccordionList>
                  {content.map((item, key) => (
                    <AccordionItem
                      inView={true}
                      key={key}
                      id={`${id}-item-${key}`}
                      linkId={`${linkId}-item-${key}`}
                      title={item.title}
                      content={item.content}
                      beginOpen={key === defaultOpen}
                    />
                  ))}
                </AccordionList>
              </GridItem>
            )}
            {button && (
              <GridItem
                tiny={12}
                tabletP={loc === 'en-US' ? 12 : 10}
                tabletPStart={loc === 'en-US' ? 1 : 2}
                tabletL={8}
                tabletLStart={3}
                desk={6}
                deskStart={4}
              >
                <AccordionLink alignLeft={true}>
                  <Spacer size={[30, 52]} />
                  <AnimateSlideText id={`${id}-cta`} delay={0.2}>
                    <Button
                      href={button.externalLink}
                      variant="underline"
                      color={colors.dark}
                    >
                      {button.label}
                    </Button>
                  </AnimateSlideText>
                </AccordionLink>
              </GridItem>
            )}
          </Grid>
        </Container>

        {bottomPadding && <Spacer size={[100, 250]} />}
      </AccordionWrapper>
    </ScrollSection>
  )
}

AccordionSection.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.object,
  content: PropTypes.array,
  button: PropTypes.object,
  faq: PropTypes.bool,
  bottomPadding: PropTypes.bool,
  defaultOpen: PropTypes.number,
  background: PropTypes.string,
}

AccordionSection.defaultProps = {
  defaultOpen: -1,
}

export default AccordionSection
